import { cn } from "@/shared/util/cn";
import { ReactElement, ReactNode } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";

export interface Tab {
    label: string;
    icon?: string | ReactElement | ReactNode;
    iconPosition?: "bottom" | "top" | "end" | "start";
    href: string;
    hidden?: boolean;
    pattern?: string;
}

export interface NavTabsProps {
    tabs: Tab[];
    centered?: boolean;
    variant?: "standard" | "scrollable" | "fullWidth";
    scrollButtons?: boolean | "auto";
}

export default function NavTabs(props: NavTabsProps) {
    const location = useLocation();
    const { tabs, variant = "standard" } = props;

    const visibleTabs = tabs.filter((t) => !t.hidden);

    return (
        <div className="flex border-t border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/80 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/80">
            <nav className="flex min-w-0 flex-1">
                <ul
                    className={cn(
                        "no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto p-2",
                        variant === "scrollable"
                            ? "flex-nowrap"
                            : "flex-nowrap md:justify-center"
                    )}
                >
                    {visibleTabs.map((tab, index) => {
                        const isActive =
                            matchPath(
                                tab.pattern ?? tab.href,
                                location.pathname
                            ) !== null;

                        return (
                            <li key={index} className="shrink-0">
                                <Link
                                    to={tab.href}
                                    className={cn(
                                        "inline-flex items-center gap-2 whitespace-nowrap rounded-2xl px-3.5 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-primary text-white shadow-soft dark:bg-primary dark:text-white"
                                            : "text-[var(--color-mockup-text-mid)] hover:bg-primary/10 hover:text-[var(--color-mockup-text)] dark:text-[var(--color-mockup-text-light)] dark:hover:bg-primary/15 dark:hover:text-[var(--color-mockup-text)]"
                                    )}
                                >
                                    {tab.icon && (
                                        <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                                            {tab.icon as ReactNode}
                                        </span>
                                    )}
                                    {tab.label}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </div>
    );
}
